import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Clock, CheckCircle2, XCircle, RefreshCw } from 'lucide-react';
import { PRODUCTOS } from '../constants/productos';
import { fmtMoney } from '../utils/money';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ESTADO = {
  pendiente: { icon: Clock, cls: 'bg-amber-50 text-amber-700 border-amber-200', label: 'Esperando autorización' },
  aprobada: { icon: CheckCircle2, cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: 'Aprobada' },
  rechazada: { icon: XCircle, cls: 'bg-red-50 text-red-700 border-red-200', label: 'Rechazada' },
};

export function describirProductos(productos) {
  return Object.entries(productos || {})
    .filter(([, v]) => v)
    .map(([k, v]) => `${PRODUCTOS.find(p => String(p.clave) === String(k))?.nombre || k} ${v}`)
    .join(', ');
}

export function horaMx(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Mexico_City' });
}

// Solicitudes de venta a crédito y abono del punto (turno activo + pendientes anteriores).
export function SolicitudesCreditoPunto({ puntoVentaId, tipo, titulo = 'Mis solicitudes a administración' }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const cargar = useCallback(async () => {
    if (!puntoVentaId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/solicitudes-credito/punto/${puntoVentaId}`);
      setItems(tipo ? data.filter(s => s.tipo === tipo) : data);
    } catch { /* sin conexión: se deja la lista anterior */ }
    setLoading(false);
  }, [puntoVentaId, tipo]);

  useEffect(() => { cargar(); }, [cargar]);

  if (!items.length) return null;
  return (
    <div className="space-y-2" data-testid="solicitudes-credito-punto">
      <div className="flex items-center justify-between">
        <p className="text-xs text-zinc-500 font-medium uppercase tracking-wide">{titulo}</p>
        <button onClick={cargar} className="text-zinc-400 p-1" title="Actualizar">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>
      {items.map(s => {
        const e = ESTADO[s.status] || ESTADO.pendiente;
        const Icon = e.icon;
        return (
          <div key={s.id} className="p-3 bg-white border border-zinc-200 rounded-xl">
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-semibold text-zinc-900 truncate">{s.tipo_label} · {s.cliente_nombre}</p>
              <span className={`shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-medium ${e.cls}`}>
                <Icon className="w-3 h-3" /> {e.label}
              </span>
            </div>
            <p className="text-sm font-mono font-bold text-zinc-800 mt-1">
              {fmtMoney(s.tipo === 'venta_credito' ? s.total : s.monto)}
            </p>
            {s.tipo === 'venta_credito' && <p className="text-xs text-zinc-500">{describirProductos(s.productos)}</p>}
            {s.status === 'rechazada' && (
              <p className="text-xs text-red-700 mt-1">Motivo: {s.motivo_rechazo}</p>
            )}
            <p className="text-[11px] text-zinc-400 mt-1">
              Solicitada {horaMx(s.created_at)}
              {s.resuelto_at && ` · ${s.status === 'aprobada' ? 'aprobó' : 'rechazó'} ${s.resuelto_por_nombre} ${horaMx(s.resuelto_at)}`}
            </p>
          </div>
        );
      })}
    </div>
  );
}
